import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { LoggerService } from './logger.service';

const LAST_USERNAME_KEY = 'lastUsername';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private _storage: Storage | null = null;
  private initialized = false;

  constructor(private logger: LoggerService) {
    this.init();
  }

  async init() {
    try {
      if (this.initialized) {
        return;
      }

      const storage = new Storage();
      await storage.create();
      this._storage = storage;
      this.initialized = true;
    } catch (error) {
      this.logger.error('Error initializing storage:', error);
      this.initialized = false;
      this._storage = null;
    }
  }

  private async ensureInitialized() {
    if (!this.initialized || !this._storage) {
      await this.init();
    }
  }

  async saveLastUsername(username: string) {
    try {
      await this.ensureInitialized();
      
      if (!username) {
        return;
      }

      await this._storage?.set(LAST_USERNAME_KEY, username);
    } catch (error) {
      this.logger.error('Error saving username:', error);
      throw error;
    }
  }

  async getLastUsername(): Promise<string | null> {
    try {
      await this.ensureInitialized();
      return await this._storage?.get(LAST_USERNAME_KEY) || null;
    } catch (error) {
      this.logger.error('Error getting last username:', error);
      return null;
    }
  }

  async clearLastUsername() {
    try {
      await this.ensureInitialized();
      await this._storage?.remove(LAST_USERNAME_KEY);
    } catch (error) {
      this.logger.error('Error clearing last username:', error);
      throw error;
    }
  }

  /** Generic Ionic Storage read (used by offline pull-test store). */
  async get<T>(key: string): Promise<T | null> {
    try {
      await this.ensureInitialized();
      const value = await this._storage?.get(key);
      return (value ?? null) as T | null;
    } catch (error) {
      this.logger.error(`Error reading storage key ${key}:`, error);
      return null;
    }
  }

  /** Generic Ionic Storage write (used by offline pull-test store). */
  async set<T>(key: string, value: T): Promise<void> {
    try {
      await this.ensureInitialized();
      await this._storage?.set(key, value);
    } catch (error) {
      this.logger.error(`Error writing storage key ${key}:`, error);
      throw error;
    }
  }

  /** Generic Ionic Storage remove (used by offline pull-test store). */
  async remove(key: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await this._storage?.remove(key);
    } catch (error) {
      this.logger.error(`Error removing storage key ${key}:`, error);
      throw error;
    }
  }

  /** All keys in Ionic Storage (used to purge prefixed offline draft rows). */
  async listKeys(): Promise<string[]> {
    try {
      await this.ensureInitialized();
      const keys = await this._storage?.keys();
      return Array.isArray(keys) ? keys : [];
    } catch (error) {
      this.logger.error('Error listing storage keys:', error);
      return [];
    }
  }
} 